// src/pages/Home.js (or wherever your Home.js lives)
import React from "react";
import "./Home.css";
import { Card, Badge, Button, SectionHead, CopyRow } from "../components/UI";
import { useToast } from "../components/ToastHost";
import { FiArrowRight, FiGlobe } from "react-icons/fi";
import { FaInstagram, FaWhatsapp } from "react-icons/fa";

import heroSky from "../Images/Home/building-sky.jpeg";
import heroBuilding from "../Images/Home/building-minds.png";
import illustration1 from "../Images/Home/Teaching.svg";
import illustration2 from "../Images/Home/exam.svg";
import illustration3 from "../Images/Home/reading.svg";
import illustration4 from "../Images/Home/consulting.svg";
import illustration5 from "../Images/Home/homework.svg";
import illustration6 from "../Images/Home/uni.svg";

import { FiPhone } from "react-icons/fi";


/**
 * ✅ ONLY flyer info (same content you had)
 * Added tone per item to get the colored borders you wanted.
 */
const MISSION_ITEMS = [
  {
    q: "Personalized Tutoring for All Ages (3–18+)",
    a: "Tailored sessions in all subjects to match your child's learning pace and style.",
    tag: "Tutoring",
    tone: "cyan",
  },
  {
    q: "Diagnostic Tests",
    a: "Detailed assessments to identify areas of improvement.",
    tag: "Assessment",
    tone: "magenta",
  },
  {
    q: "Student Counseling",
    a: "Guidance on managing student life and academic challenges.",
    tag: "Support",
    tone: "yellow",
  },
  {
    q: "University Application Assistance",
    a: "Expert help to navigate college admissions.",
    tag: "Admissions",
    tone: "green",
  },
  {
    q: "Homework Clubs",
    a: "A supportive environment for completing assignments.",
    tag: "Homework",
    tone: "orange",
  },
  {
    q: "Reading Clubs",
    a: "Cultivating a lifelong love for reading.",
    tag: "Reading",
    tone: "red",
  },
];

const REGISTER_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSekY0f-1MVUQxv7zL_LX-unCKOvZoH3YoJtUavxOEAG6hLRrQ/viewform?pli=1";

const TERMS = [
  { k: "Term 1", v: "September - December" },
  { k: "Winter Term", v: "December - January" },
  { k: "Term 2", v: "January - March" },
  { k: "Term 3", v: "April - June" },
  { k: "Summer Term", v: "July - August" },
];

const DEADLINES = [
  { d: "November 6th", note: "(Term 1, Winter)" },
  { d: "February 6th", note: "(Term 2)" },
  { d: "May 6th", note: "(Term 3, Summer Term)" },
];

export default function Home() {
  const toast = useToast();

  const phones = [
    { label: "WhatsApp", value: "+973 39460386" },
    { label: "WhatsApp", value: "+973 37945946" },
    { label: "WhatsApp", value: "+973 66331677" },
    { label: "Phone", value: "+973 17004487" },
  ];

  const socials = [
    { label: "minds.bh", icon: <FaInstagram />, href: "https://instagram.com/minds.bh" },
    { label: "mathmindslc", icon: <FaInstagram />, href: "https://instagram.com/mathmindslc" },
  ];

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.push("success", "Copied");
    } catch {
      toast.push("error", "Copy failed");
    }
  };

  const openWhatsApp = (phone) => {
    const digits = phone.replace(/[^\d+]/g, "");
    window.open(`https://wa.me/${digits.replace("+", "")}`, "_blank", "noreferrer");
  };

  const openCall = (phone) => {
    const digits = phone.replace(/[^\d+]/g, "");
    // tel: works great on mobile, and on desktop will use FaceTime/Skype/etc if available
    window.location.href = `tel:${digits}`;
  };

  return (
    <div id="top">
      {/* HERO */}
      <section className="homeHeroFull">
        <div className="heroFullStage">
          <img className="heroFullSky" src={heroSky} alt="Sky" />

          <div className="heroFullText" aria-label="Empowering Minds">
            <span className="heroWord heroWordTop">Empowering</span>
            <span className="heroWord heroWordBottom">Minds</span>
          </div>

          <div className="heroRightInfo" aria-label="What we offer">
            <div className="heroRightKicker">Minds Educational Support</div>
            <div className="heroRightTitle">Where knowledge meets nurture.</div>

            <div className="heroRightChips">
              <span className="heroChip">Personalized tutoring (3–18+)</span>
              <span className="heroChip">Diagnostic tests</span>
              <span className="heroChip">Student counseling</span>
              <span className="heroChip">University applications</span>
              <span className="heroChip">Homework clubs</span>
              <span className="heroChip">Reading clubs</span>
            </div>

            <div className="heroRightMeta">
              <span className="heroMetaPill">15+ years of experience</span>
              <span className="heroMetaDot">•</span>
              <span className="heroMetaText">Founded in Bahrain</span>
              <span className="heroMetaDot">•</span>
              <span className="heroMetaText">Serving students worldwide</span>
            </div>
          </div>

          <img className="heroFullBuilding" src={heroBuilding} alt="Minds building" />
        </div>
      </section>

      {/* OUR MISSION (NEW) */}
      <section id="mission" className="section sectionAlt anchorSection missionModernSection">
        <div className="container">
          <SectionHead
            eyebrow="OUR MISSION"
            title="Minds Educational Support"
            subtitle="Where Knowledge Meets Nurture"
            align="center"
          />

          <div className="missionOneWrap">
            <Card className="missionOneCard">
              <div className="missionOneTop">
                <div>
                  <div className="missionOneTitle">About us</div>
                  <div className="missionOneSub">Welcome to Minds Educational Support</div>
                </div>

                <div className="missionOneMeta">
                  <Badge tone="brand">3–18+</Badge>
                  <span className="missionOneMetaDot">•</span>
                  <span className="missionOneMetaText">15+ years of experience</span>
                </div>
              </div>

              <div className="missionOneAbout">
                For over 15 years, Minds Educational Support has been at the forefront of providing high class educational
                services in Bahrain and beyond. Our dedication to fostering academic excellence and personal growth in
                students from ages 3 to 18+ has set us apart as a beacon of learning and support.
              </div>

              <div className="missionOneDivider" />

              <div className="missionOneServicesHead">
                <div className="missionOneServicesTitle">Services</div>
                <div className="missionOneServicesHint">Explore what we offer</div>
              </div>

              <div className="missionServiceButtons" aria-label="Services list">
                {MISSION_ITEMS.map((x) => {
                  // pick illustration per service
                  const artMap = {
                    Tutoring: illustration1,
                    Assessment: illustration2,
                    Reading: illustration3,
                    Support: illustration4,
                    Homework: illustration5,
                    Admissions: illustration6,
                  };

                  const artSrc = artMap[x.tag];

                  return (
                    <div
                      key={x.tag}
                      className={`missionServiceBtn isStatic ${artSrc ? "hasArt" : ""}`}
                      data-tone={x.tone}
                      data-tag={x.tag}  // <-- used for per-card positioning in CSS
                      aria-label={x.q}
                      role="group"
                    >
                      {/* Background illustration */}
                      {artSrc && (
                        <img className="missionServiceArt" src={artSrc} alt="" aria-hidden="true" />
                      )}

                      {/* Content */}
                      <div className="missionServiceContent">
                        <div className="missionServiceBtnTag">{x.tag}</div>
                        <div className="missionServiceBtnTitle">{x.q}</div>
                        <div className="missionServiceBtnDesc">{x.a}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="missionOneCtaRow">
                <div className="missionOneCtaText">Register now and take the first step towards academic success and personal growth.</div>

                <Button
                  as="button"
                  type="button"
                  variant="primary"
                  iconRight={<FiArrowRight />}
                  onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  Contact us
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* TERMS + FINANCIALS */}
      <section id="terms" className="section anchorSection enrollSection">
        <div className="container">
          <SectionHead
            eyebrow="ENROLL WITH US"
            title="Term dates & details"
            subtitle="We operate on a term basis, with five terms throughout the year tailored to meet your child’s educational timeline:"
          />

          <div className="enrollGrid">
            {/* LEFT: Term Schedule */}
            <Card className="enrollCard">
              <div className="enrollCardTop">
                <div>
                  <div className="enrollTitle">Term schedule</div>
                  <div className="enrollSub">Five terms throughout the year</div>
                </div>
                <Badge tone="brand"><span className="nowrap-tag">5 terms</span></Badge>
              </div>

              <div className="enrollList">
                {TERMS.map((t) => (
                  <div className="enrollRow" key={t.k}>
                    <div className="enrollKey">{t.k}</div>
                    <div className="enrollVal">{t.v}</div>
                  </div>
                ))}
              </div>

              <div className="enrollFoot">
                <span className="enrollFootPill">Program Duration: Approximately 3 months</span>
                <span className="enrollFootDot">•</span>
                <span className="enrollFootText">Payments are made in advance</span>
              </div>
            </Card>

            {/* RIGHT: Financials & Scheduling + Deadlines */}
            <Card className="enrollCard">
              <div className="enrollCardTop">
                <div>
                  <div className="enrollTitle">Financials & scheduling</div>
                  <div className="enrollSub">Key deadlines to keep in mind</div>
                </div>
                <Badge tone="warn"><span className="nowrap-tag">Deadlines</span></Badge>
              </div>

              <div className="enrollNoteBox">
                The cost of our services is contingent upon the number of days a week your child attends. We strive to provide flexible scheduling options to accommodate.
              </div>

              <div className="enrollDeadlineGrid" aria-label="Enrollment deadlines">
                {DEADLINES.map((x) => (
                  <div className="enrollDeadline" key={x.d}>
                    <div className="enrollDeadlineDate">{x.d}</div>
                    <div className="enrollDeadlineNote">{x.note}</div>
                  </div>
                ))}
              </div>

              <div className="enrollCta">
                <div className="enrollCtaText">Questions about placement or scheduling?</div>
                <Button
                  as="button"
                  type="button"
                  variant="primary"
                  iconRight={<FiArrowRight />}
                  onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  Contact us
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* REGISTER */}
      <section id="register" className="section sectionAlt anchorSection registerSection">
        <div className="container">
          <SectionHead
            eyebrow="HOW TO REGISTER"
            title="Secure your place"
            subtitle="Please follow the steps below."
          />

          <div className="registerGrid">
            {/* Left: Steps */}
            <Card className="registerCard">
              <div className="registerTop">
                <div>
                  <div className="registerTitle">How to register</div>
                  <div className="registerSub">Simple, guided onboarding</div>
                </div>
                <Badge tone="brand"><span className="nowrap-tag">Step-by-step</span></Badge>
              </div>

              <div className="registerSteps" aria-label="Registration steps">
                <div className="registerStep">
                  <div className="registerStepIndex">1</div>
                  <div className="registerStepBody">
                    <div className="registerStepT">Inquire</div>
                    <div className="registerStepD">Contact us to get started.</div>
                  </div>
                </div>

                <div className="registerStep">
                  <div className="registerStepIndex">2</div>
                  <div className="registerStepBody">
                    <div className="registerStepT">Booking form</div>
                    <div className="registerStepD">
                      Fill out the booking form that will be sent upon inquiry.
                    </div>
                  </div>
                </div>

                <div className="registerStep">
                  <div className="registerStepIndex">3</div>
                  <div className="registerStepBody">
                    <div className="registerStepT">Multiple children</div>
                    <div className="registerStepD">
                      If you have multiple children, please fill in a form for each child separately.
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="registerNotePill">
                BOOKING FORM • MINDS REVIEW
              </div> */}
            </Card>

            {/* Right: CTA */}
            <Card className="registerCard registerCardCta">
              <div className="registerTop">
                <div>
                  <div className="registerTitle">Join our community of learners</div>
                  <div className="registerSub">Ready when you are</div>
                </div>
                <Badge tone="warn"><span className="nowrap-tag">Start now</span></Badge>
              </div>

              <div className="registerCtaText">
                Register now and take the first step towards academic success and personal growth.
              </div>

              <div className="registerCtaRow">
                <Button
                  as="a"
                  href={REGISTER_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                  variant="primary"
                  className="registerBtn"
                  iconRight={<FiArrowRight />}
                >
                  Open registration form
                </Button>

                <div className="registerCtaHint">We’ll reply with the booking form details.</div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CONTACT + SOCIALS + LEGAL */}
      <section id="contact" className="section anchorSection contactModernSection">
        <div className="container">
          <SectionHead eyebrow="FOLLOW US ON OUR SOCIALS" title="Contact" subtitle="Minds Educational Support" />

          <div className="contactGridModern">
            {/* CONTACT */}
            <Card className="contactCardModern">
              <div className="contactTopModern">
                <div>
                  <div className="contactTitleModern">Contact</div>
                  <div className="contactSubModern">Call or WhatsApp</div>
                </div>
                <Badge tone="brand">Fast reply</Badge>
              </div>

              <div className="contactRowsModern">
                {phones.map((p) => (
                  <div className="contactRowModern" key={p.value}>
                    <div className="contactRowLeft">
                      <CopyRow label={p.label} value={p.value} onCopy={copy} />
                    </div>

                    {p.label.toLowerCase().includes("whatsapp") ? (
                      <Button variant="soft" className="actionBtnModern" onClick={() => openWhatsApp(p.value)}>
                        <span className="actionIconModern">
                          <FaWhatsapp />
                        </span>
                        WhatsApp
                      </Button>
                    ) : (
                      <Button variant="soft" className="actionBtnModern" onClick={() => openCall(p.value)}>
                        <span className="actionIconModern">
                          <FiPhone />
                        </span>
                        Call
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <a className="siteModern" href="http://mindsbh.com/" target="_blank" rel="noreferrer">
                <FiGlobe />
                <span>http://mindsbh.com/</span>
                <span className="siteGo">→</span>
              </a>
            </Card>

            {/* SOCIALS + LEGAL */}
            <Card className="contactCardModern">
              <div className="contactTopModern">
                <div>
                  <div className="contactTitleModern">Socials</div>
                  <div className="contactSubModern">Follow for updates</div>
                </div>
                <Badge tone="warn">Instagram</Badge>
              </div>

              <div className="socialsModern">
                {socials.map((s) => (
                  <a className="socialLinkModern" key={s.label} href={s.href} target="_blank" rel="noreferrer">
                    <span className="socialIconModern">{s.icon}</span>
                    <span className="socialLabelModern">{s.label}</span>
                    <span className="socialGoModern">→</span>
                  </a>
                ))}
              </div>

              <div className="legalModern">
                <div className="legalTopModern">LEGAL DISCLAIMER</div>
                <div className="legalBodyModern">
                  The information contained in this brochure is subject to change without notice. Please contact us or visit our website
                  for the most up-to-date information.
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section id="careers" className="section sectionAlt anchorSection careersSection">
        <div className="container">
          <SectionHead
            eyebrow="CAREERS"
            title="Available for hiring"
            subtitle="Send your application by email"
          />

          <div className="careersGrid">
            <Card className="careersCard">
              <div className="careersTop">
                <div>
                  <div className="careersTitle">We’re accepting applications</div>
                  <div className="careersSub">Tutoring • Support • Administration</div>
                </div>
                <Badge tone="brand">
                  <span className="nowrap-tag now-hiring-tag">Now hiring</span>
                </Badge>
              </div>

              <div className="careersBody">
                <div className="careersLine">
                  <span className="careersDot" aria-hidden="true" />
                  <div className="careersText">
                    Email us at <span className="careersEmail">minds.bhr@gmail.com</span>
                  </div>
                </div>

                <div className="careersLine">
                  <span className="careersDot" aria-hidden="true" />
                  <div className="careersText">Please include your CV/Resume in the email.</div>
                </div>
              </div>

              <div className="careersCtaRow">
                <Button
                  as="a"
                  href="mailto:xxxxx@xxxxx.com?subject=Application%20-%20Minds%20Educational%20Support&body=Hi%20Minds%20Team%2C%0A%0AI%E2%80%99d%20like%20to%20apply.%20Please%20find%20my%20CV%2FResume%20attached.%0A%0AThank%20you%2C"
                  variant="primary"
                  iconRight={<FiArrowRight />}
                >
                  Email application
                </Button>

                <Button
                  as="button"
                  type="button"
                  variant="soft"
                  onClick={() => copy("xxxxx@xxxxx.com")}
                >
                  Copy email
                </Button>

                <div className="careersHint">We’ll reply with next steps.</div>
              </div>
            </Card>
          </div>
        </div>
      </section>

    </div>
  );
}